import { useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { supabase } from "../supabaseClient";
import { Key } from "react-bootstrap-icons";

// Messages Supabase les plus courants, traduits ; les autres sont affichés tels quels.
const errorMessages = {
  weak_password: "Mot de passe trop faible : choisissez-en un plus long ou plus complexe.",
  same_password: "Le nouveau mot de passe doit être différent de l'ancien.",
  reauthentication_not_valid: "Code de confirmation invalide ou expiré.",
};

// Modification du mot de passe de l'utilisateur connecté.
const PasswordSettings = () => {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  // Code reçu par email, demandé seulement si Supabase exige une réauthentification (option « Secure password change »).
  const [nonce, setNonce] = useState("");
  const [nonceRequired, setNonceRequired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const reset = () => {
    setPassword("");
    setConfirmation("");
    setNonce("");
    setNonceRequired(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    if (password !== confirmation) {
      setErrorMsg("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser(
        nonceRequired ? { password, nonce: nonce.trim() } : { password }
      );
      if (error?.code === "reauthentication_needed") {
        const { error: reauthError } = await supabase.auth.reauthenticate();
        if (reauthError) throw reauthError;
        setNonceRequired(true);
        setSuccessMsg("Par sécurité, un code de confirmation vient de vous être envoyé par email.");
        return;
      }
      if (error) throw error;
      reset();
      setSuccessMsg("Mot de passe modifié.");
    } catch (error) {
      setErrorMsg(errorMessages[error?.code] ?? error?.message ?? String(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h5 className="account-card-title"><Key size={20} />Mot de passe</h5>
      <p className="text-muted">Modifiez le mot de passe utilisé pour vous connecter.</p>
      <Form onSubmit={onSubmit}>
        <Row>
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="newPassword">
              <Form.Label>Nouveau mot de passe</Form.Label>
              <Form.Control
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="confirmPassword">
              <Form.Label>Confirmation</Form.Label>
              <Form.Control
                type="password"
                autoComplete="new-password"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                isInvalid={confirmation.length > 0 && confirmation !== password}
                required
              />
              <Form.Control.Feedback type="invalid">
                Les deux mots de passe ne correspondent pas.
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        {nonceRequired && (
          <Form.Group className="mb-3" controlId="passwordNonce">
            <Form.Label>Code reçu par email</Form.Label>
            <Form.Control
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={nonce}
              onChange={(e) => setNonce(e.target.value)}
              style={{ maxWidth: 160 }}
              autoFocus
              required
            />
          </Form.Group>
        )}
        {errorMsg && (
          <Alert variant="danger" onClose={() => setErrorMsg("")} dismissible>
            {errorMsg}
          </Alert>
        )}
        {successMsg && (
          <Alert variant="success" onClose={() => setSuccessMsg("")} dismissible>
            {successMsg}
          </Alert>
        )}
        <div className="d-flex justify-content-end">
          <Button type="submit" disabled={busy || !password || !confirmation}>
            {busy ? "Modification..." : "Modifier le mot de passe"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default PasswordSettings;
